import React from 'react'
import {
  Dialog as Modal,
  Button,
  LoadingSpinner,
  Link,
  Sheet
} from '@paypalcorp/pp-react'
import { GET_HONEY_OFFER_DETAILS } from './queries'
import { Action, OfferDetailsType } from './types/ShoppingPageType'
import { Breakpoint } from './lib/useBreakpoint'
import { get } from 'lodash'
import { graphql } from './services/graphql'
import {
  OFFER_DETAIL_CTA_CLICKED,
  SHOPPING_MERCHANT_CODE_USE_PRESSED,
  SHOPPING_MERCHANT_CODE_SCREEN_SHOWN,
  SHOPPING_MERCHANT_CODE_CLOSE_PRESSED,
  SHOPPING_MERCHANT_DEAL_USE_PRESSED
} from './lib/client-constants'
import {
  FptiEventNames,
  FptiClick,
  FptiDataType,
  sendFptiClick,
  sendFptiImpression
} from './lib/fpti'
import { useAsyncEffect } from './lib/useAsyncEffect'
import useWorldReady, { FormatMessage } from './lib/useWorldReady'
import ReadMoreText from './ReadMoreText'
import { getModalContent } from './HoneyOfferDetailsModalContent'

interface Props {
  isOpen: boolean
  onClose: Function
  honeyOfferId: string
  honeyStoreId: string
  breakpoint: Breakpoint
  isMockMode?: boolean
  mockData?: OfferDetailsType
  csrf: string
  offerType: string
  affiliateTagUrl: string // temporary property for affiliate link frontend fix story: DTSHOPATMS-1503
}

type OfferModalFptiDataType = {
  merchant_name: string
  source: string
  merchant_id: string
  store_id: string
  offer_program_id: string
  page_variant?: string
  link_name?: string
  expiry_date: string
  incentive_offer_value: string
}

export const getModalSections = (
  sections: {
    content: OfferDetailsType['details']
    displayShowMore?: boolean
  }[],
  fptiClick: FptiClick,
  formatMessage: FormatMessage
) => {
  if (sections.length > 0) {
    return (
      <div className="offer-details-modal__sections-container">
        {sections.map((section, key) => {
          return (
            <div
              className="offer-details-modal__section"
              key={`modal-section-${key}`}
            >
              <div className="offer-details-modal__section__title ppvx_text--body-strong">
                {get(section, 'content.title') || ''}
              </div>
              {section.displayShowMore ? (
                <ReadMoreText
                  entireText={get(section, 'content.content') || ''}
                  readMoreText={formatMessage(
                    'main.shoppingHub.modal.offerDetails.showMore'
                  )}
                  showLessText={formatMessage(
                    'main.shoppingHub.modal.offerDetails.showLess'
                  )}
                  fptiEvent={() => {
                    fptiClick(OFFER_DETAIL_CTA_CLICKED, { link_name: 'expand' })
                  }}
                />
              ) : (
                <div
                  className="offer-details-modal__section__content ppvx_text--body"
                  dangerouslySetInnerHTML={{
                    __html: get(section, 'content.content') || ''
                  }}
                />
              )}
            </div>
          )
        })}
      </div>
    )
  }
  return null
}

const HoneyOfferDetailsModal = ({
  isOpen,
  onClose,
  honeyOfferId,
  honeyStoreId,
  breakpoint,
  isMockMode = false,
  mockData,
  csrf,
  offerType,
  affiliateTagUrl // temporary property for affiliate link frontend fix story: DTSHOPATMS-1503
}: Props) => {
  const { formatMessage } = useWorldReady()

  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [offerDetails, setOfferDetails] = React.useState<
    OfferDetailsType | undefined
  >()
  const { isMobile } = breakpoint
  const STORE_DETAILS_PAGE = 'Store Details Page'

  let fptiEventInfo: OfferModalFptiDataType = {
    merchant_name: '',
    source: '',
    merchant_id: '',
    store_id: '',
    offer_program_id: '',
    page_variant: '',
    expiry_date: '',
    incentive_offer_value: ''
  }

  const fptiClick: FptiClick = (eventName, optionalEventData) => {
    const conditionContent = get(offerDetails, 'conditions[0].content') || ''
    fptiEventInfo = {
      source: STORE_DETAILS_PAGE,
      merchant_name: get(offerDetails, 'title') || '',
      merchant_id: get(offerDetails, 'storeIds[0]') || '',
      store_id: get(offerDetails, 'storeIds[1]') || honeyStoreId,
      offer_program_id:
        get(offerDetails, 'decryptedOfferProgramId') || honeyOfferId,
      page_variant: get(offerDetails, 'actions[0].title') || '',
      expiry_date: conditionContent.replace('Ends ', '') || '',
      incentive_offer_value: get(offerDetails, 'subtitle') || ''
    }

    const payload: FptiDataType = {
      ...fptiEventInfo,
      ...optionalEventData,
      event_name: eventName
    }

    sendFptiClick(payload)
  }

  const fptiImpression = (eventName: FptiEventNames): void => {
    const payload: FptiDataType = {
      ...fptiEventInfo,
      event_name: eventName
    }

    sendFptiImpression(payload)
  }

  const handleCloseModal = (e?: React.SyntheticEvent<Element, Event>) => {
    onClose()
    fptiClick(SHOPPING_MERCHANT_CODE_CLOSE_PRESSED, {
      dismiss_method: e ? 'close' : 'outside'
    })
  }

  /*UseAsyncEffect prevents executing what is inside runAsync
  if isOpen state changes before graphql promise gets resolved
  */
  useAsyncEffect(
    (runAsync: Function) => {
      if (isOpen) {
        if (isMockMode) {
          setTimeout(() => {
            runAsync(() => {
              setOfferDetails(mockData)
              setIsLoading(false)
            })
          }, 500)
        } else {
          graphql(
            GET_HONEY_OFFER_DETAILS,
            { honeyOfferId, honeyStoreId },
            null,
            csrf
          )
            .then((res: any) => {
              return runAsync(() => {
                let offer = get(res, 'data.data.uiShopping.offers.items[0]')
                if (offer) {
                  const conditionContent =
                    get(offer, 'conditions[0].content') || ''
                  setOfferDetails(offer)
                  setIsLoading(false)
                  fptiEventInfo = {
                    source: STORE_DETAILS_PAGE,
                    merchant_name: get(offer, 'title') || '',
                    merchant_id: get(offer, 'storeIds[0]') || '',
                    store_id: get(offer, 'storeIds[1]') || honeyStoreId,
                    offer_program_id:
                      get(offer, 'decryptedOfferProgramId') || honeyOfferId,
                    page_variant: get(offer, 'actions[0].title') || '',
                    expiry_date: conditionContent.replace('Ends ', '') || '',
                    incentive_offer_value: get(offer, 'subtitle') || ''
                  }
                  fptiImpression(SHOPPING_MERCHANT_CODE_SCREEN_SHOWN)
                } else {
                  handleCloseModal()
                }
              })
            })
            .catch(() => {
              runAsync(() => {
                handleCloseModal()
              })
            })
        }
      } else {
        setIsLoading(true)
      }
    },
    [isOpen]
  )

  const getOfferButton = (actions: OfferDetailsType['actions']) => {
    if (actions && actions.length > 0) {
      const navigationFound = actions.find(
        (e: Action) => e.type === 'NAVIGATION'
      )
      if (navigationFound) {
        return (
          <Link
            // affiliate link frontend fix story: DTSHOPATMS-1503
            // the next line should be un-commented once fixed
            // href={get(actions, '[0].navigationTarget', '') || ''}
            href={affiliateTagUrl}
            target="_blank"
            rel="noreferrer noopener"
            onClick={() => {
              const eventName =
                offerType === 'sale'
                  ? SHOPPING_MERCHANT_DEAL_USE_PRESSED
                  : SHOPPING_MERCHANT_CODE_USE_PRESSED
              fptiClick(eventName, { link_name: 'action' })
            }}
          >
            <Button className="offer-details-modal__button">
              {actions[0].title}
            </Button>
          </Link>
        )
      }
    }
    return null
  }

  if (isOpen) {
    if (isLoading) {
      if (isMobile) {
        return (
          <Sheet
            className="offer-details-modal"
            wrapperClassName="offer-details-modal-sheet"
            containerClassName="offer-details-modal-sheet__container"
            footerAlign="center"
            hideTitle
            isOpen={isOpen}
            title="Loading"
            onCloseComplete={handleCloseModal}
            closeButtonProps={{ className: 'ppvx_icon-button' }}
          >
            <div className="offer-details-modal__spinner">
              <LoadingSpinner screenReaderText="loading" size="xl" />
            </div>
          </Sheet>
        )
      }
      return (
        <Modal
          hideTitle
          title="Loading"
          isOpen={isOpen}
          center
          footerAlign="center"
          closeOnBackgroundClick
          containerClassName="offer-details-modal"
          onCloseComplete={handleCloseModal}
          closeButtonProps={{ className: 'ppvx_icon-button' }}
        >
          <div className="offer-details-modal__spinner">
            <LoadingSpinner screenReaderText="loading" size="xl" />
          </div>
        </Modal>
      )
    }
    if (offerDetails) {
      if (isMobile) {
        return (
          <Sheet
            className="offer-details-modal"
            wrapperClassName="offer-details-modal-sheet"
            containerClassName="offer-details-modal-sheet__container"
            footerAlign="center"
            footerContents={getOfferButton(offerDetails.actions)}
            hideTitle
            isOpen={isOpen}
            title={offerDetails.title}
            onCloseComplete={handleCloseModal}
            closeButtonProps={{ className: 'ppvx_icon-button' }}
          >
            {getModalContent(offerDetails, offerType, fptiClick, formatMessage)}
          </Sheet>
        )
      }
      return (
        <Modal
          hideTitle
          title={offerDetails.title}
          isOpen={isOpen}
          center
          footerAlign="center"
          closeOnBackgroundClick
          containerClassName="offer-details-modal"
          onCloseComplete={handleCloseModal}
          footerContents={getOfferButton(offerDetails.actions)}
          footerClassName="offer-details-modal__footer"
          closeButtonProps={{ className: 'ppvx_icon-button' }}
        >
          {getModalContent(offerDetails, offerType, fptiClick, formatMessage)}
        </Modal>
      )
    }
  }
  return null
}

export default HoneyOfferDetailsModal
